<template>
    <div class="knowledge-graph-editor-jointjs">
      <div class="editor-header">
        <h2>知识点关系图编辑器 (JointJS)</h2>
        <div class="header-actions">
          <button class="btn-primary" @click="saveGraph">保存关系图</button>
          <button class="btn-secondary" @click="clearGraph">清空画板</button>
          <button class="btn-secondary" @click="showJsonModal = true">查看JSON</button>
          <button class="btn-secondary" @click="exportImage">导出图片</button>
        </div>
      </div>
  
      <div class="editor-container">
        <!-- 知识点列表 -->
        <div class="knowledge-points-panel">
          <h3>知识点列表</h3>
          <div class="search-box">
            <input 
              type="text" 
              v-model="searchKeyword" 
              placeholder="查找知识点..."
              @input="filterKnowledgePoints"
            >
          </div>
          <div class="points-list">
            <div 
              v-for="point in filteredKnowledgePoints" 
              :key="point.kpId"
              class="knowledge-point-item"
              draggable="true"
              @dragstart="onDragStart($event, point)"
            >
              <div class="point-circle" :style="{ backgroundColor: getPointColor(point.category) }">
                {{ point.name.charAt(0) }}
              </div>
              <div class="point-info">
                <div class="point-name">{{ point.name }}</div>
                <div class="point-category">{{ point.category }}</div>
              </div>
            </div>
          </div>
        </div>
  
        <!-- JointJS 画板区域 -->
        <div class="canvas-container">
          <div class="canvas-header">
            <h3>关系图画板 (JointJS)</h3>
            <div class="canvas-tools">
              <button class="btn-tool" @click="toggleAddEdgeMode">
                {{ addEdgeMode ? '退出连接模式' : '连接模式' }}
              </button>
              <button class="btn-tool" @click="deleteSelected">删除选中</button>
              <button class="btn-tool" @click="fitView">适应画布</button>
              <button class="btn-tool" @click="zoomIn">放大</button>
              <button class="btn-tool" @click="zoomOut">缩小</button>
            </div>
          </div>
          
          <div 
            class="canvas" 
            ref="canvas"
            @dragover="onDragOver"
            @drop="onDrop"
          >
            <!-- JointJS 画布将在这里渲染 -->
          </div>
        </div>
      </div>
  
      <!-- JSON展示模态框 -->
      <div class="modal" v-if="showJsonModal">
        <div class="modal-content">
          <h3>关系图JSON数据</h3>
          <div class="json-container">
            <pre>{{ graphJson }}</pre>
          </div>
          <div class="modal-actions">
            <button class="btn-primary" @click="copyJson">复制JSON</button>
            <button class="btn-secondary" @click="showJsonModal = false">关闭</button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { dia, shapes } from '@joint/core'
  
  export default {
    name: 'KnowledgeGraphEditorJointJS',
    data() {
      return {
        knowledgePoints: [],
        filteredKnowledgePoints: [],
        searchKeyword: '',
        graph: null,
        paper: null,
        addEdgeMode: false,
        showJsonModal: false,
        nextNodeId: 1,
        nextEdgeId: 1,
        selectedNode: null,
        selectedEdge: null,
        sourceNode: null
      }
    },
    computed: {
      graphJson() {
        if (!this.graph) return '{}'
        
        const cells = this.graph.getCells()
        const nodes = []
        const edges = []
        
        cells.forEach(cell => {
          if (cell.isElement()) {
            nodes.push({
              id: cell.id,
              kpId: cell.attributes.kpId,
              name: cell.attributes.name,
              category: cell.attributes.category,
              x: cell.position().x,
              y: cell.position().y
            })
          } else if (cell.isLink()) {
            edges.push({
              id: cell.id,
              source: cell.getSourceElement()?.id,
              target: cell.getTargetElement()?.id
            })
          }
        })
        
        return JSON.stringify({ nodes, edges }, null, 2)
      }
    },
    created() {
      //检查是否登录
      if (!this.$root.isLoggedIn) {
        this.$goToLogin(this.$route.fullPath)
        return
      }
      this.fetchKnowledgePoints()
    },
    mounted() {
      this.initGraph()
    },
    beforeUnmount() {
      if (this.paper) {
        this.paper.remove()
      }
    },
    methods: {
      async fetchKnowledgePoints() {
        try {
          const token = localStorage.getItem('jwt_token')
          const response = await fetch('/api/v1/knowledge/points/list', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              page: 1,
              pageSize: 1000,
              keyword: ''
            })
          })
          const data = await response.json()
          if (data.code === 200) {
            this.knowledgePoints = data.data.list
            this.filteredKnowledgePoints = [...this.knowledgePoints]
          } else {
            this.$message.error(data.message || '获取知识点列表失败')
          }
        } catch (error) {
          console.error('Failed to fetch knowledge points:', error)
          this.$message.error('获取知识点列表失败')
        }
      },
      filterKnowledgePoints() {
        if (!this.searchKeyword.trim()) {
          this.filteredKnowledgePoints = [...this.knowledgePoints]
        } else {
          this.filteredKnowledgePoints = this.knowledgePoints.filter(point =>
            point.name.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
            point.category.toLowerCase().includes(this.searchKeyword.toLowerCase())
          )
        }
      },
      initGraph() {
        const container = this.$refs.canvas
        
        // 创建图实例
        this.graph = new dia.Graph()
        
        // 创建画布
        this.paper = new dia.Paper({
          el: container,
          model: this.graph,
          width: container.clientWidth,
          height: container.clientHeight,
          gridSize: 10,
          drawGrid: true,
          background: {
            color: '#fafafa'
          },
          interactive: {
            vertexAdd: false,
            vertexMove: false,
            vertexRemove: false,
            arrowheadMove: false
          },
          defaultConnector: { name: 'rounded' },
          defaultRouter: { name: 'manhattan' },
          validateConnection: (sourceView, sourceMagnet, targetView, targetMagnet) => {
            // 防止自连接
            console.log(sourceView, targetView)
            console.log(sourceMagnet, targetMagnet) 
            if (sourceView === targetView) return false
            return true
          }
        })

        // 绑定事件
        this.bindEvents()
        
        // 窗口大小变化
        window.addEventListener('resize', this.handleResize)
      },
      bindEvents() {
        // 节点点击事件
        this.paper.on('element:pointerclick', (elementView) => {
          const element = elementView.model
          
          if (this.addEdgeMode) {
            if (this.sourceNode === element.id) {
              // 取消选择
              this.sourceNode = null
              element.attr('body/fill', this.getPointColor(element.attributes.category))
            } else if (this.sourceNode) {
              // 创建连接
              this.createEdge(this.sourceNode, element.id)
              const sourceElement = this.graph.getCell(this.sourceNode)
              if (sourceElement) {
                sourceElement.attr('body/fill', this.getPointColor(sourceElement.attributes.category))
              }
              this.sourceNode = null
              this.addEdgeMode = false
            } else {
              // 选择源节点
              this.sourceNode = element.id
              element.attr('body/fill', '#ffa500')
            }
          } else {
            // 选择节点
            this.clearSelection()
            element.attr('body/stroke', '#ff4d4f')
            element.attr('body/strokeWidth', 3)
          }
        })

        // 边点击事件
        this.paper.on('link:pointerclick', (linkView) => {
          const link = linkView.model
          this.clearSelection()
          link.attr('line/stroke', '#ff4d4f')
          link.attr('line/strokeWidth', 3)
        })

        // 画布点击事件
        this.paper.on('blank:pointerclick', () => {
          if (!this.addEdgeMode) {
            this.clearSelection()
          }
        })
      },
      clearSelection() {
        this.graph.getElements().forEach(element => {
          element.attr('body/stroke', '#d9d9d9')
          element.attr('body/strokeWidth', 1)
        })
        this.graph.getLinks().forEach(link => {
          link.attr('line/stroke', '#666')
          link.attr('line/strokeWidth', 2)
        })
      },
      handleResize() {
        if (this.paper) {
          const container = this.$refs.canvas
          this.paper.setDimensions(container.clientWidth, container.clientHeight)
        }
      },
      onDragStart(event, point) {
        event.dataTransfer.setData('text/plain', JSON.stringify(point))
      },
      onDragOver(event) {
        event.preventDefault()
      },
      onDrop(event) {
        event.preventDefault()
        const pointData = JSON.parse(event.dataTransfer.getData('text/plain'))
        const rect = this.$refs.canvas.getBoundingClientRect()
        const x = event.clientX - rect.left
        const y = event.clientY - rect.top
        
        this.addNode(pointData, x, y)
      },
      addNode(point, x, y) {
        const rect = new shapes.standard.Rectangle({
          position: { x, y },
          size: { width: 120, height: 60 },
          attrs: {
            body: {
              fill: this.getPointColor(point.category),
              stroke: '#d9d9d9',
              strokeWidth: 1,
              rx: 8,
              ry: 8
            },
            label: {
              text: point.name,
              fill: '#fff',
              fontSize: 12,
              fontWeight: 'bold',
              textAnchor: 'middle',
              textVerticalAnchor: 'middle'
            }
          },
          kpId: point.kpId,
          name: point.name,
          category: point.category
        })
        
        this.graph.addCell(rect)
      },
      createEdge(fromNodeId, toNodeId) {
        // 检查是否已存在连接
        const links = this.graph.getLinks()
        const existingLink = links.find(link => 
          link.getSourceElement()?.id === fromNodeId && 
          link.getTargetElement()?.id === toNodeId
        )
        
        if (existingLink) {
          this.$message.warning('连接已存在')
          return
        }
        
        const link = new shapes.standard.Link({
          source: { id: fromNodeId },
          target: { id: toNodeId },
          attrs: {
            line: {
              stroke: '#666',
              strokeWidth: 2
            }
          },
          labels: [{
            position: 0.5,
            attrs: {
              text: {
                text: '',
                fill: '#666',
                fontSize: 10
              }
            }
          }]
        })
        
        this.graph.addCell(link)
      },
      toggleAddEdgeMode() {
        this.addEdgeMode = !this.addEdgeMode
        if (!this.addEdgeMode) {
          this.sourceNode = null
          this.clearSelection()
        }
      },
      deleteSelected() {
        const selectedElements = this.graph.getElements().filter(element => 
          element.attr('body/stroke') === '#ff4d4f'
        )
        const selectedLinks = this.graph.getLinks().filter(link => 
          link.attr('line/stroke') === '#ff4d4f'
        )
        
        selectedElements.forEach(element => {
          element.remove()
        })
        
        selectedLinks.forEach(link => {
          link.remove()
        })
      },
      clearGraph() {
        if (confirm('确定要清空整个画板吗？')) {
          this.graph.clear()
          this.nextNodeId = 1
          this.nextEdgeId = 1
          this.selectedNode = null
          this.addEdgeMode = false
          this.sourceNode = null
        }
      },
      fitView() {
        this.paper.fitToContent({
          padding: 50,
          allowNewOrigin: 'any'
        })
      },
      zoomIn() {
        const currentZoom = this.paper.scale().sx
        this.paper.scale(currentZoom * 1.2, currentZoom * 1.2)
      },
      zoomOut() {
        const currentZoom = this.paper.scale().sx
        this.paper.scale(currentZoom * 0.8, currentZoom * 0.8)
      },
      saveGraph() {
        const data = this.graph.toJSON()
        console.log('保存的关系图数据:', data)
        this.$message.success('关系图已保存')
      },
      exportImage() {
        const svg = this.paper.svg
        const serializer = new XMLSerializer()
        const svgString = serializer.serializeToString(svg)
        
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        const img = new Image()
        
        img.onload = () => {
          canvas.width = img.width
          canvas.height = img.height
          ctx.fillStyle = '#fff'
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.drawImage(img, 0, 0)
          
          const link = document.createElement('a')
          link.download = 'knowledge-graph.png'
          link.href = canvas.toDataURL()
          link.click()
        }
        
        const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
        const url = URL.createObjectURL(svgBlob)
        img.src = url
      },
      copyJson() {
        navigator.clipboard.writeText(this.graphJson).then(() => {
          this.$message.success('JSON已复制到剪贴板')
        }).catch(() => {
          this.$message.error('复制失败')
        })
      },
      getPointColor(category) {
        const colors = {
          '数学': '#FF6B6B',
          '物理': '#4ECDC4',
          '化学': '#45B7D1',
          '生物': '#96CEB4',
          '历史': '#FFEAA7',
          '地理': '#DDA0DD',
          '语文': '#98D8C8',
          '英语': '#F7DC6F',
          '政治': '#BB8FCE',
          '技术': '#85C1E9'
        }
        return colors[category] || '#95A5A6'
      }
    }
  }
  </script>
  
  <style scoped>
  .knowledge-graph-editor-jointjs {
    padding: 20px;
    height: calc(100vh - 120px);
    display: flex;
    flex-direction: column;
  }
  
  .editor-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 10px;
    border-bottom: 1px solid #eee;
  }
  
  .header-actions {
    display: flex;
    gap: 10px;
  }
  
  .editor-container {
    display: flex;
    flex: 1;
    gap: 20px;
    min-height: 0;
  }
  
  .knowledge-points-panel {
    width: 300px;
    border: 1px solid #ddd;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  }
  
  .knowledge-points-panel h3 {
    margin: 0;
    padding: 15px;
    background-color: #f5f5f5;
    border-bottom: 1px solid #ddd;
    border-radius: 8px 8px 0 0;
  }
  
  .search-box {
    padding: 15px;
    border-bottom: 1px solid #eee;
  }
  
  .search-box input {
    width: 100%;
    padding: 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
  }
  
  .points-list {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
  }
  
  .knowledge-point-item {
    display: flex;
    align-items: center;
    padding: 10px;
    margin-bottom: 8px;
    border: 1px solid #eee;
    border-radius: 6px;
    cursor: grab;
    transition: all 0.2s;
  }
  
  .knowledge-point-item:hover {
    background-color: #f9f9f9;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  .point-circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-weight: bold;
    margin-right: 10px;
    flex-shrink: 0;
  }
  
  .point-info {
    flex: 1;
    min-width: 0;
  }
  
  .point-name {
    font-weight: 500;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  .point-category {
    font-size: 12px;
    color: #666;
  }
  
  .canvas-container {
    flex: 1;
    border: 1px solid #ddd;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
  }
  
  .canvas-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 15px;
    background-color: #f5f5f5;
    border-bottom: 1px solid #ddd;
    border-radius: 8px 8px 0 0;
  }
  
  .canvas-header h3 {
    margin: 0;
  }
  
  .canvas-tools {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }
  
  .canvas {
    flex: 1;
    position: relative;
    background-color: #fafafa;
    overflow: hidden;
  }
  
  .btn-primary,
  .btn-secondary,
  .btn-tool {
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s;
  }
  
  .btn-primary {
    background-color: #2196F3;
    color: white;
  }
  
  .btn-primary:hover {
    background-color: #1976D2;
  }
  
  .btn-secondary {
    background-color: #f5f5f5;
    color: #333;
    border: 1px solid #ddd;
  }
  
  .btn-secondary:hover {
    background-color: #e0e0e0;
  }
  
  .btn-tool {
    background-color: #FF9800;
    color: white;
    font-size: 12px;
    padding: 6px 12px;
  }
  
  .btn-tool:hover {
    background-color: #F57C00;
  }
  
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  }
  
  .modal-content {
    background-color: white;
    padding: 20px;
    border-radius: 8px;
    width: 80%;
    max-width: 800px;
    max-height: 80vh;
    overflow-y: auto;
  }
  
  .json-container {
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 15px;
    margin: 15px 0;
    max-height: 400px;
    overflow-y: auto;
  }
  
  .json-container pre {
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
    font-family: 'Courier New', monospace;
    font-size: 12px;
  }
  
  .modal-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 20px;
  }
  </style>